import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Warning, SealCheck, ArrowSquareOut, Trash, Plus } from "@phosphor-icons/react";

const SERVICE_TYPES = [
  { value: "car", label: "Car / Detailing" },
  { value: "industrial", label: "Industrial / Machine" },
];
const SERVICE_STYLES = [
  { value: "mobile", label: "Mobile" },
  { value: "in_shop", label: "In-shop" },
];

function PhotoRow({ photo, onChange, onRemove, index }) {
  return (
    <div className="flex items-center gap-2" data-testid={`listing-photo-row-${index}`}>
      <input value={photo} onChange={onChange} placeholder="https://..." className="input-rugged flex-1" data-testid={`listing-photo-input-${index}`} />
      <button type="button" onClick={onRemove} className="tag hover:border-[#FF5722]" data-testid={`listing-photo-remove-${index}`}>
        <Trash size={14} weight="bold" />
      </button>
    </div>
  );
}

export default function MyListing() {
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/me/listing")
      .then((r) => { setListing(r.data); setForm(toForm(r.data)); })
      .catch((e) => setError(formatApiError(e)));
  }, []);

  function toForm(d) {
    return {
      business_name: d.business_name || "",
      logo_url: d.logo_url || "",
      photos: (d.photos || []).join("\n") ? d.photos : [],
      about: d.about || "",
      city: d.city || "",
      state: d.state || "",
      zip: d.zip || "",
      address: d.address || "",
      show_address: !!d.show_address,
      service_types: d.service_types || [],
      service_style: d.service_style || [],
      service_radius_mi: d.service_radius_mi || 50,
      sub_services: d.sub_services || [],
      phone: d.phone || "",
      contact_email: d.contact_email || "",
      website: d.website || "",
    };
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      // normalise empty photos
      payload.photos = (form.photos || []).filter((p) => (p || "").trim());
      payload.sub_services = (form.sub_services || []).filter((s) => (s || "").trim());
      payload.service_radius_mi = Number(form.service_radius_mi) || 50;
      const { data } = await api.put("/me/listing", payload);
      setListing(data);
      setForm(toForm(data));
      toast.success("Listing saved");
    } catch (e) {
      setError(formatApiError(e));
      toast.error("Save failed");
    }
    setSaving(false);
  };

  if (user && (user.level || 0) < 2) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="listing-level-gate">
          <div className="section-label mb-3">— UPGRADE REQUIRED</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase tracking-tight">Listings are a Level 2 feature</h1>
          <p className="font-plex text-[#9CA3AF] mt-4">Upgrade to <strong className="text-[#00D4FF]">Network Pro</strong> to get a public profile on the US customer map and receive direct leads.</p>
          <Link to="/billing?level=2" className="btn-primary mt-6 inline-flex" data-testid="listing-upgrade-cta">Upgrade to Network Pro</Link>
        </div>
      </Layout>
    );
  }

  if (!listing || !form) {
    return <Layout><div className="max-w-3xl mx-auto px-4 py-20 font-mono text-xs text-[#52525B] text-center">[LOADING LISTING]</div></Layout>;
  }

  const pending = listing.pending_changes || {};
  const hasPending = Object.keys(pending).length > 0;

  return (
    <Layout>
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="section-label mb-2">— MY LISTING</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="listing-headline">
              Your Public Profile
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {listing.approved
              ? <span className="tag tag-active" data-testid="listing-approved-tag">LIVE</span>
              : <span className="tag" data-testid="listing-pending-tag">AWAITING APPROVAL</span>}
            {listing.approved && listing.slug && (
              <a href={`/pro/${listing.slug}`} target="_blank" rel="noreferrer" className="btn-secondary text-xs" data-testid="listing-view-public">
                View public page <ArrowSquareOut size={12} weight="bold" />
              </a>
            )}
          </div>
        </div>

        {hasPending && (
          <div className="surface p-5 mb-6 border-[#FF5722]/40" data-testid="listing-pending-banner">
            <div className="flex items-center gap-2 mb-1">
              <Warning size={18} weight="fill" className="text-[#FF5722]" />
              <span className="font-chivo font-bold text-white uppercase tracking-tight">Pending admin approval</span>
            </div>
            <p className="font-plex text-sm text-[#9CA3AF]">
              The following changes are queued and will go live once an admin reviews them: <code className="text-[#FF5722]">{Object.keys(pending).join(", ")}</code>
            </p>
          </div>
        )}

        <form onSubmit={save} className="space-y-6" data-testid="listing-form">
          {/* IDENTITY (admin approval) */}
          <div className="surface p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">BUSINESS IDENTITY</div>
              <span className="font-mono text-[10px] text-[#FF5722] tracking-widest">ADMIN APPROVAL REQUIRED</span>
            </div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Business name</label>
            <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="input-rugged mb-4" data-testid="listing-business-name" />
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Logo URL</label>
            <input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." className="input-rugged mb-4" data-testid="listing-logo-url" />
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Photos</label>
            {(form.photos || []).map((p, i) => (
              <PhotoRow
                key={i}
                photo={p}
                index={i}
                onChange={(e) => {
                  const next = [...form.photos];
                  next[i] = e.target.value;
                  set("photos", next);
                }}
                onRemove={() => set("photos", form.photos.filter((_, idx) => idx !== i))}
              />
            ))}
            <button type="button" onClick={() => set("photos", [...(form.photos || []), ""])} className="btn-secondary text-xs mt-2" data-testid="listing-photo-add">
              <Plus size={12} weight="bold" /> Add photo
            </button>
          </div>

          {/* LIVE-EDITABLE */}
          <div className="surface p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">CONTACT & SERVICE AREA</div>
              <span className="font-mono text-[10px] text-[#00D4FF] tracking-widest">LIVE INSTANTLY</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Phone</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-rugged" data-testid="listing-phone" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Lead email</label>
                <input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="input-rugged" data-testid="listing-contact-email" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Website</label>
                <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." className="input-rugged" data-testid="listing-website" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Service radius (miles)</label>
                <input type="number" min={5} max={500} value={form.service_radius_mi} onChange={(e) => set("service_radius_mi", e.target.value)} className="input-rugged" data-testid="listing-radius" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">City</label>
                <input value={form.city} onChange={(e) => set("city", e.target.value)} className="input-rugged" data-testid="listing-city" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">State (2-letter)</label>
                <input maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} className="input-rugged" data-testid="listing-state" />
              </div>
              <div>
                <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Zip</label>
                <input maxLength={5} inputMode="numeric" pattern="[0-9]*" value={form.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} className="input-rugged" data-testid="listing-zip" />
              </div>
              <div className="flex items-center gap-2 mt-7">
                <input id="show_address" type="checkbox" checked={form.show_address} onChange={(e) => set("show_address", e.target.checked)} data-testid="listing-show-address" />
                <label htmlFor="show_address" className="font-plex text-sm text-[#9CA3AF]">Show street address publicly</label>
              </div>
            </div>

            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mt-4 mb-2">Street address (shown only if box above is checked)</label>
            <input value={form.address} onChange={(e) => set("address", e.target.value)} className="input-rugged" data-testid="listing-address" />

            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mt-4 mb-2">About (describe your services, crew, certifications)</label>
            <textarea rows={5} value={form.about} onChange={(e) => set("about", e.target.value)} className="input-rugged" data-testid="listing-about" />

            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mt-4 mb-2">Service types</label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_TYPES.map((t) => (
                <button type="button" key={t.value}
                        onClick={() => toggle("service_types", t.value)}
                        className={`tag ${form.service_types.includes(t.value) ? "tag-active" : ""}`}
                        data-testid={`listing-service-type-${t.value}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mt-4 mb-2">Service style</label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_STYLES.map((t) => (
                <button type="button" key={t.value}
                        onClick={() => toggle("service_style", t.value)}
                        className={`tag ${form.service_style.includes(t.value) ? "tag-active" : ""}`}
                        data-testid={`listing-service-style-${t.value}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mt-4 mb-2">Sub-services (one per line)</label>
            <textarea rows={3} value={(form.sub_services || []).join("\n")} onChange={(e) => set("sub_services", e.target.value.split("\n"))} className="input-rugged" data-testid="listing-sub-services" />
          </div>

          {error && <div className="font-mono text-xs text-[#EF4444]" data-testid="listing-error">{error}</div>}

          <div className="flex justify-end gap-3">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="listing-save-btn">
              {saving ? "Saving..." : "Save Listing"}
            </button>
          </div>

          {listing.approved && (
            <div className="font-mono text-xs text-[#52525B] text-center" data-testid="listing-live-help">
              <SealCheck size={12} weight="fill" className="inline text-[#00D4FF] mr-1" />
              Text, hours, service area and contact info go live instantly. Business name, logo and photos queue for admin approval.
            </div>
          )}
        </form>
      </div>
    </Layout>
  );
}
